class InViewport {
    constructor(options) {
        this.options = Object.assign(
            {
                selector: "[data-inviewport]",
                threshold: 0.8, // Default visibility threshold
                largeElementThreshold: 0.4, // Threshold for large elements
                activeClass: "is-visible", // Default class name
                headerOffset: 0, // Header height offset
            },
            options
        );

        this.thresholdArray = [];
    }

    /**
     * Calculates the required visibility percentage based on element size.
     * @param {HTMLElement} element - The target element
     * @returns {number} - Adjusted threshold
     */
    getThreshold(element) {
        const { threshold, largeElementThreshold } = this.options;
        return element.offsetHeight > window.innerHeight
            ? largeElementThreshold
            : threshold;
    }

    /**
     * Determines if the element is visible in the viewport.
     * @param {IntersectionObserverEntry} entry - Intersection entry
     * @returns {boolean} - Whether the element meets the visibility threshold
     */
    isElementVisible(entry) {
        const { target, boundingClientRect } = entry;
        const elementThreshold = this.getThreshold(target);

        const viewportHeight = window.innerHeight;
        const visibleHeight =
            Math.min(boundingClientRect.bottom, viewportHeight) -
            Math.max(boundingClientRect.top, 0);

        return (
            visibleHeight >=
            (target.offsetHeight > viewportHeight
                ? viewportHeight * elementThreshold
                : target.offsetHeight * elementThreshold)
        );
    }

    /**
     * Handles the visibility changes based on "data-inviewport" attribute.
     * @param {IntersectionObserverEntry} entry - Intersection entry
     * @param {IntersectionObserver} observer - Observer instance
     */
    handleVisibility(entry, observer) {
        const { activeClass } = this.options;
        const { target } = entry;

        const visibilityMode =
            target.getAttribute("data-inviewport") || "always";
        const isVisible = this.isElementVisible(entry);

        if (visibilityMode === "false") {
            observer.unobserve(target); // Stop observing the element
            return;
        }

        if (isVisible) {
            target.classList.add(activeClass);

            if (visibilityMode === "once") {
                observer.unobserve(target); // Stop observing after first visibility
            }
        } else if (
            visibilityMode === "always" &&
            entry.intersectionRatio === 0
        ) {
            target.classList.remove(activeClass); // Remove class only when the element is completely out of view
        }
    }

    /**
     * Handles invalid threshold values.
     * @returns {boolean}
     */
    handleException() {
        const { threshold, largeElementThreshold } = this.options;
        if (
            threshold < 0 ||
            threshold > 1 ||
            largeElementThreshold < 0 ||
            largeElementThreshold > 1
        ) {
            console.warn("Threshold values must be between 0 and 1.");
            return false;
        }
        return true;
    }

    /**
     * Initializes the Intersection Observer with header offset adjustments.
     */
    init() {
        const { selector, headerOffset, largeElementThreshold } = this.options;

        if (!this.handleException()) return false;

        const observer = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) =>
                    this.handleVisibility(entry, observer)
                );
            },
            {
                threshold: [0, 0.8, largeElementThreshold], // Observe all elements fully when appearing
                rootMargin: `-${headerOffset}px 0px 0px 0px`, // Adjust for fixed headers
            }
        );

        document.querySelectorAll(selector)?.forEach((element) => {
            const visibilityMode = element.getAttribute("data-inviewport");

            if (visibilityMode !== "false") {
                observer.observe(element);
            }
        });
    }
}

const inViewport = new InViewport({
    selector: "[data-inviewport]", // Target elements with data-inviewport attribute
    threshold: 0.8, // 80% visibility for small elements
    largeElementThreshold: 0.2, // 20% visibility for large elements
    activeClass: "is-visible", // Custom class name for visibility
    headerOffset: 50, // Offset for fixed headers (adjustable)
});

if (window.acf) {
    window.acf.addAction(
        "render_block_preview/name=voyager-fifty-fifty",
        setTimeout(() => {
            inViewport.init();
        }, 2000)
    );
} else {
    window.addEventListener("DOMContentLoaded", () => {
        inViewport.init();
    });
}
